import axios from 'axios';
import {
    AiReportChatMessage,
    AiReportColumnMapping,
    AiReportConversion,
    AiReportCustomFill,
    AiReportHistoryItem,
    AiReportSavedTemplate,
    AiReportSummary,
    AiReportTemplateColumn,
} from '../models/AiReportHelper';

const API_URL = process.env.REACT_APP_SERVER_URL + "/app/ai-report-helper";

const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('gpcwstoken_kw')}`,
});

export const AiReportHelperService = {
    uploadSource: async (file: File): Promise<{
        success: boolean;
        conversion: AiReportConversion;
        headers: string[];
        sampleRows: Record<string, any>[];
        rowCount: number;
    }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post(`${API_URL}/upload-source`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    uploadTemplate: async (conversionId: number, file: File): Promise<{
        success: boolean;
        conversion: AiReportConversion;
        headers: string[];
    }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post(`${API_URL}/${conversionId}/upload-template`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    buildTemplate: async (conversionId: number, columns: string[]): Promise<{
        success: boolean;
        conversion: AiReportConversion;
        headers: string[];
    }> => {
        const response = await axios.post(`${API_URL}/${conversionId}/build-template`, { columns }, { headers: authHeader() });
        return response.data;
    },

    suggestMapping: async (conversionId: number): Promise<{
        success: boolean;
        conversion: AiReportConversion;
        mapping: AiReportColumnMapping;
    }> => {
        const response = await axios.post(`${API_URL}/${conversionId}/suggest-mapping`, {}, { headers: authHeader() });
        return response.data;
    },

    resolveFillInstruction: async (
        conversionId: number,
        targetColumn: string,
        instruction: string
    ): Promise<{ success: boolean; customFill: AiReportCustomFill }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/fill-instruction`,
            { targetColumn, instruction },
            { headers: authHeader() }
        );
        return response.data;
    },

    suggestFillOptions: async (
        conversionId: number,
        targetColumn: string,
        instruction?: string
    ): Promise<{ success: boolean; confident: boolean; options?: string[]; reason?: string }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/suggest-fill-options`,
            { targetColumn, instruction },
            { headers: authHeader() }
        );
        return response.data;
    },

    generate: async (
        conversionId: number,
        selectedColumns: string[],
        mapping: AiReportColumnMapping,
        columnRenames?: Record<string, string>
    ): Promise<{ success: boolean; conversion: AiReportConversion; summary: AiReportSummary; rowCount: number; droppedColumnCount: number }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/generate`,
            { selectedColumns, mapping, columnRenames },
            { headers: authHeader() }
        );
        return response.data;
    },

    previewDatasetApply: async (
        conversionId: number,
        datasetId: number,
        matchSourceColumn: string,
        datasetColumnKeys: string[]
    ): Promise<{
        success: boolean;
        referredColumnLabel: string;
        totalRows: number;
        matchedCount: number;
        unmatchedCount: number;
        unmatchedSamples: string[];
        matchedSampleRows: { matchValue: string; values: Record<string, string> }[];
    }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/preview-dataset-apply`,
            { datasetId, matchSourceColumn, datasetColumnKeys },
            { headers: authHeader() }
        );
        return response.data;
    },

    addTemplateColumns: async (
        conversionId: number,
        labels: string[]
    ): Promise<{ success: boolean; conversion: AiReportConversion; newColumns: AiReportTemplateColumn[] }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/add-template-columns`,
            { labels },
            { headers: authHeader() }
        );
        return response.data;
    },

    resolveEditInstruction: async (
        conversionId: number,
        targetColumnLabel: string,
        instruction: string
    ): Promise<{ success: boolean; customFill: AiReportCustomFill }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/resolve-edit`,
            { targetColumnLabel, instruction },
            { headers: authHeader() }
        );
        return response.data;
    },

    applyEdit: async (
        conversionId: number,
        targetColumnLabel: string,
        customFill: AiReportCustomFill
    ): Promise<{ success: boolean; conversion: AiReportConversion; summary: AiReportSummary; messages: AiReportChatMessage[] }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/apply-edit`,
            { targetColumnLabel, customFill },
            { headers: authHeader() }
        );
        return response.data;
    },

    downloadGeneratedReport: async (conversionId: number): Promise<Blob> => {
        const response = await axios.get(`${API_URL}/${conversionId}/download`, {
            headers: authHeader(),
            responseType: 'blob',
        });
        return response.data;
    },

    chat: async (
        conversionId: number,
        message: string
    ): Promise<{ success: boolean; reply: string; messages: AiReportChatMessage[] }> => {
        const response = await axios.post(`${API_URL}/${conversionId}/chat`, { message }, { headers: authHeader() });
        return response.data;
    },

    getHistory: async (limit: number = 20): Promise<{ success: boolean; history: AiReportHistoryItem[] }> => {
        const response = await axios.get(`${API_URL}/history`, { params: { limit }, headers: authHeader() });
        return response.data;
    },

    getById: async (conversionId: number): Promise<{
        success: boolean;
        conversion: AiReportConversion;
        messages: AiReportChatMessage[];
    }> => {
        const response = await axios.get(`${API_URL}/${conversionId}`, { headers: authHeader() });
        return response.data;
    },

    deleteById: async (conversionId: number): Promise<void> => {
        await axios.delete(`${API_URL}/${conversionId}`, { headers: authHeader() });
    },

    previewFile: async (
        conversionId: number,
        which: 'source' | 'template' | 'generated'
    ): Promise<{ success: boolean; headers: string[]; rows: Record<string, any>[]; totalRows: number; truncated: boolean }> => {
        const response = await axios.get(`${API_URL}/${conversionId}/preview/${which}`, { headers: authHeader() });
        return response.data;
    },

    getAnalyticsData: async (
        conversionId: number,
        params: { xColumn: string; yColumn?: string; aggregation?: 'sum' | 'count'; groupBy?: 'day' | 'week' | 'month' | 'year' }
    ): Promise<{ success: boolean; labels: string[]; values: number[]; isDate: boolean }> => {
        const response = await axios.get(`${API_URL}/${conversionId}/analytics-data`, {
            params,
            headers: authHeader(),
        });
        return response.data;
    },

    updateMeta: async (
        conversionId: number,
        patch: { title?: string; generated_file_name?: string }
    ): Promise<{ success: boolean; conversion: AiReportConversion }> => {
        const response = await axios.patch(`${API_URL}/${conversionId}`, patch, { headers: authHeader() });
        return response.data;
    },

    classifyIntent: async (message: string): Promise<{ success: boolean; intent: 'analysis' | 'other' }> => {
        const response = await axios.post(`${API_URL}/classify-intent`, { message }, { headers: authHeader() });
        return response.data;
    },

    listSavedTemplates: async (): Promise<{ success: boolean; templates: AiReportSavedTemplate[] }> => {
        const response = await axios.get(`${API_URL}/saved-templates`, { headers: authHeader() });
        return response.data;
    },

    saveAsTemplate: async (
        conversionId: number,
        name: string,
        templateHeaders: AiReportTemplateColumn[],
        mapping: AiReportColumnMapping,
        selectedColumns: string[]
    ): Promise<{ success: boolean; template: AiReportSavedTemplate }> => {
        const response = await axios.post(
            `${API_URL}/saved-templates`,
            { conversionId, name, templateHeaders, mapping, selectedColumns },
            { headers: authHeader() }
        );
        return response.data;
    },

    uploadSavedTemplateFile: async (file: File): Promise<{
        success: boolean;
        templateFileName: string;
        templateFileUrl: string;
        templateHeaders: AiReportTemplateColumn[];
    }> => {
        const form = new FormData();
        form.append('file', file);
        const response = await axios.post(`${API_URL}/saved-templates/upload-file`, form, {
            headers: { 'Content-Type': 'multipart/form-data', ...authHeader() },
        });
        return response.data;
    },

    addSavedTemplate: async (
        name: string,
        templateFileName: string,
        templateFileUrl: string,
        templateHeaders: AiReportTemplateColumn[],
        selectedColumns: string[]
    ): Promise<{ success: boolean; template: AiReportSavedTemplate }> => {
        const response = await axios.post(
            `${API_URL}/saved-templates`,
            { name, templateFileName, templateFileUrl, templateHeaders, selectedColumns },
            { headers: authHeader() }
        );
        return response.data;
    },

    renameSavedTemplate: async (templateId: number, name: string): Promise<{ success: boolean; template: AiReportSavedTemplate }> => {
        const response = await axios.patch(
            `${API_URL}/saved-templates/${templateId}`,
            { name },
            { headers: authHeader() }
        );
        return response.data;
    },

    loadSavedTemplate: async (
        conversionId: number,
        savedTemplateId: number
    ): Promise<{ success: boolean; conversion: AiReportConversion; selectedColumns: string[]; droppedColumnCount: number }> => {
        const response = await axios.post(
            `${API_URL}/${conversionId}/load-saved-template`,
            { savedTemplateId },
            { headers: authHeader() }
        );
        return response.data;
    },

    deleteSavedTemplate: async (templateId: number): Promise<void> => {
        await axios.delete(`${API_URL}/saved-templates/${templateId}`, { headers: authHeader() });
    },
};
